import { format, parseISO } from 'date-fns';
import type { Invoice } from '../types';
import {
  getCustomerName,
  getContractDisplay,
  getUserName,
} from '../types';

/**
 * Escapes a value for CSV format
 * Handles special characters, newlines, and CSV injection prevention
 */
export function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);

  // Prevent CSV injection by prefixing potentially dangerous characters
  if (/^[=+\-@\t\r]/.test(str)) {
    str = "'" + str;
  }

  // Escape quotes and wrap in quotes if needed
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }

  return str;
}

/**
 * Formats a date string for CSV export
 */
function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  try {
    return format(parseISO(dateStr), 'yyyy-MM-dd');
  } catch {
    return '';
  }
}

/**
 * Column definitions for CSV export
 */
const COLUMNS = [
  {
    key: 'invoice_number',
    label: 'Invoice #',
    getValue: (inv: Invoice) => inv.invoice_number,
  },
  {
    key: 'status',
    label: 'Status',
    getValue: (inv: Invoice) => inv.status,
  },
  {
    key: 'created_at',
    label: 'Created At',
    getValue: (inv: Invoice) => formatDate(inv.created_at),
  },
  {
    key: 'date',
    label: 'Invoice Date',
    getValue: (inv: Invoice) => formatDate(inv.date),
  },
  {
    key: 'expiration_date',
    label: 'Due Date',
    getValue: (inv: Invoice) => formatDate(inv.expiration_date),
  },
  {
    key: 'payment_date',
    label: 'Payment Date',
    getValue: (inv: Invoice) => formatDate(inv.payment_date),
  },
  {
    key: 'customer',
    label: 'Account',
    getValue: (inv: Invoice) => getCustomerName(inv.customer),
  },
  {
    key: 'contract',
    label: 'Contract',
    getValue: (inv: Invoice) => getContractDisplay(inv.contract),
  },
  {
    key: 'project_name',
    label: 'Project Name',
    getValue: (inv: Invoice) => inv.project_name || '',
  },
  {
    key: 'project_number',
    label: 'Project Number',
    getValue: (inv: Invoice) => inv.project_number || '',
  },
  {
    key: 'quote_number',
    label: 'Quote Number',
    getValue: (inv: Invoice) => inv.quote_number || '',
  },
  {
    key: 'po_number',
    label: 'P/O Number',
    getValue: (inv: Invoice) => inv.po_number || '',
  },
  {
    key: 'sales_manager',
    label: 'Account Manager',
    getValue: (inv: Invoice) => getUserName(inv.sales_manager),
  },
  {
    key: 'contractual_manager',
    label: 'Contractual Manager',
    getValue: (inv: Invoice) => getUserName(inv.contractual_manager),
  },
  {
    key: 'currency',
    label: 'Currency',
    getValue: (inv: Invoice) => inv.currency || 'CAD',
  },
  {
    key: 'subtotal',
    label: 'Subtotal',
    getValue: (inv: Invoice) => (inv.subtotal_amount ?? inv.total ?? 0).toFixed(2),
  },
  {
    key: 'tax_category',
    label: 'Tax Category',
    getValue: (inv: Invoice) => inv.tax_category || 'none',
  },
  {
    key: 'tax_amount',
    label: 'Tax Amount',
    getValue: (inv: Invoice) => (inv.tax_amount ?? 0).toFixed(2),
  },
  {
    key: 'total',
    label: 'Total (incl. tax)',
    getValue: (inv: Invoice) => (inv.total ?? 0).toFixed(2),
  },
  {
    key: 'payment_terms',
    label: 'Payment Terms',
    getValue: (inv: Invoice) => inv.payment_terms || '',
  },
  {
    key: 'tracking_numbers',
    label: 'Tracking Numbers',
    getValue: (inv: Invoice) => (inv.tracking_numbers || []).join('; '),
  },
  {
    key: 'next_step',
    label: 'Next Step',
    getValue: (inv: Invoice) => inv.next_step || '',
  },
  {
    key: 'next_step_deadline',
    label: 'Next Step Deadline',
    getValue: (inv: Invoice) => formatDate(inv.next_step_deadline),
  },
  {
    key: 'hubspot_link',
    label: 'HubSpot Link',
    getValue: (inv: Invoice) => inv.hubspot_link || '',
  },
  {
    key: 'gdrive_link',
    label: 'Google Drive Link',
    getValue: (inv: Invoice) => inv.gdrive_link || '',
  },
];

/**
 * Exports invoices to a CSV file and triggers download
 */
export function exportInvoicesToCSV(invoices: Invoice[], filename = 'invoices'): void {
  // Build CSV header
  const header = COLUMNS.map((col) => escapeCSVValue(col.label)).join(',');

  // Build CSV rows
  const rows = invoices.map((invoice) =>
    COLUMNS.map((col) => escapeCSVValue(col.getValue(invoice))).join(',')
  );

  // Combine into CSV content
  const csvContent = [header, ...rows].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
